import { OrganizationMarketplaceRoom, OrganizationMarketplaceRoomUnit } from '@spike/marketplace-model';
import { Element } from 'components/Service/ChipSelector';

export interface DaycareRoomUnitOption {
    unitId: number;
    unitName: string;
    roomName: string;
    active: boolean;
    petTypeIds: Array<string>;
}

const normalizePetTypeId = (petTypeId: string | number): string => String(petTypeId).toLowerCase().trim();

/**
 * Room is addable when it shares at least one pet type with the service.
 * e.g. service = dogs → rooms that include dogs.
 */
const roomSharesPetTypeWithService = (
    roomPetTypeIds: Array<string | number>,
    selectedPetTypeIds: Array<string>
): boolean => {
    if (!selectedPetTypeIds.length) {
        return false;
    }

    const normalizedRoomIds = (roomPetTypeIds ?? []).map(normalizePetTypeId);
    if (normalizedRoomIds.length === 0) {
        return false;
    }

    const normalizedSelectedIds = selectedPetTypeIds.map(normalizePetTypeId);
    return normalizedSelectedIds.some(petTypeId => normalizedRoomIds.includes(petTypeId));
};

const isAddableRoomUnit = (unit: DaycareRoomUnitOption, selectedPetTypeIds: Array<string>): boolean =>
    unit.active && roomSharesPetTypeWithService(unit.petTypeIds, selectedPetTypeIds);

export const flattenDaycareRoomUnits = (rooms: Array<OrganizationMarketplaceRoom>): Array<DaycareRoomUnitOption> =>
    rooms.flatMap(room =>
        (room.roomUnits || []).map((unit: OrganizationMarketplaceRoomUnit) => ({
            unitId: unit.id,
            unitName: unit.name,
            roomName: room.name,
            active: unit.active === true,
            petTypeIds: (room.petTypeIds ?? []).map(normalizePetTypeId)
        }))
    );

export const sortDaycareRoomUnits = (units: Array<DaycareRoomUnitOption>): Array<DaycareRoomUnitOption> =>
    [...units].sort((left, right) => {
        const roomCompare = left.roomName.toLocaleLowerCase().localeCompare(right.roomName.toLocaleLowerCase());
        if (roomCompare !== 0) {
            return roomCompare;
        }
        return left.unitName.toLocaleLowerCase().localeCompare(right.unitName.toLocaleLowerCase());
    });

export const roomUnitToElement = (unit: DaycareRoomUnitOption): Element<number> => ({
    id: unit.unitId,
    name: `${unit.unitName} - ${unit.roomName}`
});

/** Options shown in the + Add dropdown: active units whose room shares a service pet type. */
export const buildSelectableDaycareRoomElements = (
    rooms: Array<OrganizationMarketplaceRoom>,
    selectedPetTypeIds: Array<string>
): Array<Element<number>> =>
    sortDaycareRoomUnits(
        flattenDaycareRoomUnits(rooms).filter(unit => isAddableRoomUnit(unit, selectedPetTypeIds))
    ).map(roomUnitToElement);

export const buildSelectedDaycareRoomElements = (
    rooms: Array<OrganizationMarketplaceRoom>,
    selectedRoomIds: Array<number>
): Array<Element<number>> => {
    const unitsById = new Map(flattenDaycareRoomUnits(rooms).map(unit => [unit.unitId, unit]));

    return selectedRoomIds
        .map(roomId => unitsById.get(roomId))
        .filter((unit): unit is DaycareRoomUnitOption => unit !== undefined)
        .map(roomUnitToElement);
};

export const filterCompatibleRoomIds = (
    rooms: Array<OrganizationMarketplaceRoom>,
    selectedRoomIds: Array<number>,
    selectedPetTypeIds: Array<string>
): Array<number> => {
    const compatibleIds = new Set(
        flattenDaycareRoomUnits(rooms)
            .filter(unit => roomSharesPetTypeWithService(unit.petTypeIds, selectedPetTypeIds))
            .map(unit => unit.unitId)
    );

    return selectedRoomIds.filter(roomId => compatibleIds.has(roomId));
};
